// components/projects/ProjectList.js
import React, { useState, useEffect, useCallback } from 'react';
import UserManagementScreen from '../admin/UserManagementScreen';
import TAQueueScreen from '../ta/TAQueueScreen';
import NewProjectModal from './NewProjectModal';
import ProjectDetailsModal from './ProjectDetailsModal';
import WorkflowModal from './WorkflowModal';
import { useAlert } from '../common/AppAlert';
import { WorkflowEngine } from '../../services/workflowEngine';
import { getAvailableProjectTypes, getProjectTypeDisplay, getTypeUI } from '../../config/workflows';
import { formatDate } from '../../utils/dates';

const ProjectList = ({ user, token, onLogout, isDarkMode, setIsDarkMode, api }) => {
  const { alert, confirm } = useAlert();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [error, setError] = useState('');
  const [inProgressFilter, setInProgressFilter] = useState(false);
  const [filterString, setFilterString] = useState('');
  const [selectedProjectType, setSelectedProjectType] = useState('All');
  const [sortField, setSortField] = useState('last_update');
  const [sortDirection, setSortDirection] = useState('DESC');
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showTAQueue, setShowTAQueue] = useState(false);

  const [showNewProject, setShowNewProject] = useState(false);
  const [showProjectDetails, setShowProjectDetails] = useState(false);

  const loadProjects = useCallback(async () => {
    if (!token) return;

    try {
      const params = new URLSearchParams({
        sort_field: 'project_number',
        sort_direction: 'DESC'
      });

      const data = await api.get(`/projects?${params}`, token);

      const projectsWithStatus = data.map(project => {
        if (project.project_type === 'Characterization') {
          return project;
        } else {
          const completedTaskIds = project.completed_tasks || [];
          // project.metadata carries the custom-field values that decide which tasks apply
          const status = WorkflowEngine.getProjectStatus(
            project.project_type, completedTaskIds, project.metadata
          );
          return { ...project, current_status: status };
        }
      });

      setProjects(projectsWithStatus);
      // applyFilters is intentionally NOT called here — the useEffect below picks up the
      // projects-changed signal and re-filters with current (not stale-closure) filter state.
      setError('');
    } catch (error) {
      if (error.message.includes('401')) {
        setError('Authentication failed. Please log in again.');
      } else if (error.message.includes('403')) {
        setError('Access forbidden. Please check your permissions.');
      } else {
        setError('Failed to load project data: ' + error.message);
      }
    }
  }, [token, api]);

  const applyFilters = (projectData = projects) => {
    let filtered = projectData;

    if (selectedProjectType !== 'All') {
      filtered = filtered.filter(project => project.project_type === selectedProjectType);
    }

    if (inProgressFilter) {
      filtered = filtered.filter(project => !project.current_status.includes('Complete'));
    }

    if (filterString) {
      const lowerFilter = filterString.toLowerCase();
      filtered = filtered.filter(project =>
        Object.values(project).some(value =>
          value !== null && value.toString().toLowerCase().includes(lowerFilter)
        )
      );
    }

    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'project_number') {
        aVal = parseInt(aVal) || 0;
        bVal = parseInt(bVal) || 0;
      } else if (sortField === 'priority') {
        aVal = aVal && aVal.toString().trim() ? parseInt(aVal) : 1000;
        bVal = bVal && bVal.toString().trim() ? parseInt(bVal) : 1000;
      } else if (sortField === 'project_created' || sortField === 'last_update') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else if (sortField === 'current_status') {
        // Status strings are "N - description"; extract leading step index for numeric sort
        const aMatch = (a.current_status || '').match(/^(\d+)/);
        const bMatch = (b.current_status || '').match(/^(\d+)/);
        aVal = aMatch ? parseInt(aMatch[1]) : 999;
        bVal = bMatch ? parseInt(bMatch[1]) : 999;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'ASC' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'ASC' ? 1 : -1;
      return 0;
    });

    setFilteredProjects(filtered);
  };

  useEffect(() => {
    if (token && user) {
      loadProjects();
    }
  }, [loadProjects, token, user]);

  useEffect(() => {
    applyFilters();
  }, [inProgressFilter, filterString, projects, selectedProjectType, sortField, sortDirection]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'n') {
        e.preventDefault();
        if (user.isAdmin) {
          setShowNewProject(true);
        } else {
          alert('Must be an administrator to create new projects...');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [user.isAdmin]);

  const handleFilterStringKeyPress = (e) => {
    if (e.key === 'Enter') {
      setFilterString(e.target.value);
    }
  };

  const clearFilters = () => {
    setInProgressFilter(false);
    setFilterString('');
    setSelectedProjectType('All');
    const el = document.getElementById('filterInput');
    if (el) el.value = '';
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortField(field);
      setSortDirection('ASC');
    }
  };

  const getSortIndicator = (field) => {
    if (sortField === field) {
      return sortDirection === 'ASC' ? ' ▲' : ' ▼';
    }
    return '';
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSettingsDropdown && !event.target.closest('.settings-container')) {
        setShowSettingsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettingsDropdown]);

  if (showUserManagement) {
    return (
      <UserManagementScreen
        user={user}
        token={token}
        onBack={() => setShowUserManagement(false)}
        isDarkMode={isDarkMode}
        api={api}
      />
    );
  }

  if (showTAQueue) {
    return (
      <TAQueueScreen
        user={user}
        token={token}
        onBack={() => { setShowTAQueue(false); loadProjects(); }}
        api={api}
      />
    );
  }

  if (error) {
    return <div className="error-screen">{error}</div>;
  }

  if (projects.length === 0) {
    return <div className="loading-screen">Loading...</div>;
  }

  const headers = ['Project_Number', 'Project_Created', 'Project_Type', 'Project_Name', 'Current_Status', 'Last_Update', 'Last_Updated_By', 'Notes', 'Priority', 'Link'];
  const fieldMap = {
    'Project_Number': 'project_number',
    'Project_Created': 'project_created',
    'Project_Type': 'project_type',
    'Project_Name': 'project_name',
    'Priority': 'priority',
    'Current_Status': 'current_status',
    'Last_Update': 'last_update',
    'Last_Updated_By': 'last_updated_by',
    'Notes': 'notes',
    'Link': 'report_path'
  };

  return (
    <div className="app-root">
      <div className="topbar">
        <div className="topbar-left">Logged in as: {user.displayName}</div>
        <h1 className="topbar-center">GTrack Dashboard</h1>
        <div className="topbar-right">
          {user.isAdmin && (
            <button className="btn" onClick={() => setShowUserManagement(true)}>
              User Management
            </button>
          )}
          <button className="btn" onClick={onLogout}>
            Logout
          </button>
          <div className="settings-container dropdown-container">
            <button
              className="btn-icon"
              onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
              title="Settings"
            >
              ⚙️
            </button>
            {showSettingsDropdown && (
              <div className="dropdown">
                <button
                  className="dropdown-item"
                  onClick={() => {
                    setIsDarkMode(!isDarkMode);
                    setShowSettingsDropdown(false);
                  }}
                >
                  Change Display Mode ({isDarkMode ? 'Light' : 'Dark'})
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="controls">
        <select
          className="select input-medium select-accent"
          value={selectedProjectType}
          onChange={(e) => {
            const value = e.target.value;
            // Workbench entries navigate to a dedicated dashboard; regular type entries filter in place
            if (value.startsWith('__workbench:')) {
              const wb = value.substring('__workbench:'.length);
              if (wb === 'thermal') {
                setShowTAQueue(true);
              }
              // Dropdown's controlled value (selectedProjectType) is unchanged — snap back to what it was
            } else {
              setSelectedProjectType(value);
            }
          }}
        >
          <option value="All">All Project Types</option>
          <optgroup label="Type Filters">
            {getAvailableProjectTypes().map(type => (
              <option key={type} value={type}>{getProjectTypeDisplay(type)}</option>
            ))}
          </optgroup>
          <optgroup label="Workbenches">
            {/* One workbench for both thermal types (TA + TM share a queue and a priority order) */}
            <option value="__workbench:thermal">Thermal Workbench</option>
          </optgroup>
        </select>
        <button className="btn" onClick={() => setInProgressFilter(!inProgressFilter)}>
          {inProgressFilter ? 'Show All' : 'In Progress Only'}
        </button>
        <input
          id="filterInput"
          className="input"
          type="text"
          placeholder="Filter String (press Enter)"
          onKeyPress={handleFilterStringKeyPress}
        />
        <button className="btn" onClick={clearFilters}>
          Clear Filter
        </button>
        <button
          className={`btn btn-create push-right${!user.isAdmin ? ' faux-disabled' : ''}`}
          onClick={() => user.isAdmin ? setShowNewProject(true) : alert('Must be an administrator to create new projects...')}
        >
          + Add New Project
        </button>
      </div>

      <table className="gtrack-table">
        <thead>
          <tr>
            {headers.map((header) => {
              const fieldName = fieldMap[header] || header.toLowerCase();
              return (
                <th
                  key={header}
                  onClick={() => handleSort(fieldName)}
                  title={`Click to sort by ${header}`}
                >
                  {header}{getSortIndicator(fieldName)}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {filteredProjects.map((project) => (
            <tr
              key={project.id}
              className="clickable-row"
              onClick={() => {
                setSelectedProject(project);
                setShowProjectDetails(true);
              }}
            >
              <td>{project.project_number}</td>
              <td>{new Date(project.project_created).toLocaleDateString()}</td>
              <td>{project.project_type}</td>
              <td>{project.project_name}</td>
              <td>
                {project.current_status}
                {/* active_hold_reason / active_hold_started_at come from project_list_view for every
                    type, so any project with an open hold shows the pill next to its status. */}
                {project.active_hold_reason && (
                  <span
                    className="badge-onhold badge-inline"
                    title={`On hold since ${formatDate(project.active_hold_started_at)} — ${project.active_hold_reason}`}
                  >
                    On Hold
                  </span>
                )}
              </td>
              <td>{new Date(project.last_update).toLocaleDateString()}</td>
              <td>{project.last_updated_by}</td>
              <td>{project.notes || ''}</td>
              <td>{project.priority || ''}</td>
              <td className="center">
                {project.report_path ? (
                  <a
                    href={project.report_path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >click</a>
                ) : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <NewProjectModal
        isOpen={showNewProject}
        onClose={() => setShowNewProject(false)}
        onProjectCreate={loadProjects}
        user={{ ...user, token }}
        api={api}
        /* Precedence: explicit Type Filter > last-created type (NewProjectModal remembers it) >
           NewProjectModal's own fallback (first workflow type). Invalid stored types are
           rejected by the modal's workflowTypes.includes() guard. */
        defaultType={selectedProjectType !== 'All'
          ? selectedProjectType
          : localStorage.getItem('gtrack_last_project_type') || undefined}
        onProjectCreated={(newProject) => {
          setSelectedProject(newProject);
          setShowProjectDetails(true);
        }}
      />

      {/* Types that declare a TYPE_UI entry (Thermal Analysis, Data Sheets) get the two-column
          WorkflowModal with clocks, custom fields and holds; legacy types get the plain modal. */}
      <ProjectDetailsModal
        isOpen={showProjectDetails && !getTypeUI(selectedProject?.project_type)}
        onClose={() => setShowProjectDetails(false)}
        project={selectedProject}
        user={{ ...user, token }}
        onProjectUpdate={loadProjects}
        api={api}
      />

      <WorkflowModal
        isOpen={showProjectDetails && !!getTypeUI(selectedProject?.project_type)}
        onClose={() => setShowProjectDetails(false)}
        project={selectedProject}
        user={{ ...user, token }}
        onProjectUpdate={loadProjects}
        api={api}
      />
    </div>
  );
};

export default ProjectList;
